"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, FileText, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, UserCheck, Sparkles, ShieldCheck } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const processSteps = [{
  id: "audit-step",
  stepNumber: "01",
  title: "Comprehensive SEO & Technical Audit",
  tagline: "Uncovering Hidden Bottlenecks",
  description: "I personally crawl every page on your site to discover indexation errors, crawl budget inefficiencies, content cannibalisation, and core web vital delays.",
  deliverable: "Actionable priority roadmap & personal screen-recorded video walkthrough",
  iconName: "Search",
  highlight: "Technical & On-Page"
}, {
  id: "optimization-step",
  stepNumber: "02",
  title: "Strategic Search Optimization",
  tagline: "Aligning Search Intent & Authority",
  description: "I refine your target page structures, optimize metadata, rebuild internal linking paths, and expand page copy to capture high-converting commercial searches.",
  deliverable: "Fully optimized landing pages, metadata & content briefs",
  iconName: "FileText",
  highlight: "Commercial Intent"
}, {
  id: "citations-step",
  stepNumber: "03",
  title: "Local Citations & Authority",
  tagline: "Strengthening Geographic Footprint",
  description: "I audit your existing directory listings, purge inaccurate duplicate profiles, and secure high-trust local citations to boost Map Pack prominence.",
  deliverable: "Clean citation ecosystem & NAP consistency report",
  iconName: "MapPin",
  highlight: "Map Pack Visibility"
}, {
  id: "reviews-step",
  stepNumber: "04",
  title: "Review & Reputation Strategy",
  tagline: "Building Unshakable Buyer Trust",
  description: "I implement simple, high-converting review acquisition workflows for your Google Business Profile, turning satisfied clients into public proof.",
  deliverable: "Custom automated review request scripts & guide",
  iconName: "Star",
  highlight: "Conversion Rate Growth"
}, {
  id: "schema-step",
  stepNumber: "05",
  title: "Structured Data & Schema",
  tagline: "Optimizing for Search & AI Engines",
  description: "I craft custom JSON-LD schema markup for your business entities, services, products, and FAQs—making sure search and AI engines index your precise context.",
  deliverable: "Validated custom JSON-LD schema implementation",
  iconName: "Code2",
  highlight: "Rich Snippets & AI"
}, {
  id: "reporting-step",
  stepNumber: "06",
  title: "Transparent Reporting & Strategy",
  tagline: "Focusing on Real Revenue & Leads",
  description: "Every month, we meet 1-on-1 to review organic lead volume, revenue impact, and ranking progress using clear, real-time dashboards with zero agency fluff.",
  deliverable: "Live performance dashboard & monthly strategy call",
  iconName: "BarChart3",
  highlight: "Direct Accountability"
}];
const iconMap = {
  Search: Search,
  FileText: FileText,
  MapPin: MapPin,
  Star: Star,
  Code2: Code2,
  BarChart3: BarChart3
};
export default function Process() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-header", {
        y: -20
      });
      gsap.to(".process-header", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".process-card", {
        y: 35
      });
      gsap.to(".process-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 75%",
          once: true
        }
      });
      gsap.set(".process-cta", {
        y: 20
      });
      gsap.to(".process-cta", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".process-cta",
          start: "top 90%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-header, .process-card, .process-cta", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="consultancyauditprocess" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="aria-hidden:true pointer-events-none absolute inset-0 -z-10 opacity-30">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[300px] bg-secondary/40 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 right-10 w-[400px] h-[250px] bg-accent/20 rounded-full blur-3xl" />
      </div>

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="process-header max-w-3xl mx-auto text-center mb-16 md:mb-20" style={{
        opacity: 0
      }}>
          <Badge variant="secondary" className="mb-4 px-3 py-1 bg-secondary text-secondary-foreground font-medium text-xs rounded-full border border-accent/20">
            <UserCheck className="size-3.5 text-primary" />
            <span>Hands-On Consultancy Process</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-serif">
            How You & I Work Together for Sustainable Growth
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            No middle managers or junior account executives. When you partner with YE Consultancy, you work directly with me—Yassine Essaadi—through a transparent, 6-step search strategy tailored to your exact business goals.
          </p>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {processSteps.map((step, i) => {
          const Icon = iconMap[step.iconName as keyof typeof iconMap];
          const isLastInRow = (i + 1) % 3 === 0;
          return <div data-index={i} key={step.id} className="process-card relative flex flex-col h-full" style={{
            opacity: 0
          }}>
                {!isLastInRow && i < processSteps.length - 1 && <div className="hidden lg:block absolute top-12 -right-4 w-8 h-[2px] bg-gradient-to-r from-accent/50 to-secondary z-10" />}

                <Card className="flex flex-col justify-between h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl overflow-hidden group">
                  <CardHeader className="p-6 pb-4 relative">
                    <div className="flex items-center justify-between mb-4">
                      <span className="inline-flex items-center justify-center font-mono font-bold text-sm h-9 px-3 rounded-lg bg-primary text-primary-foreground shadow-xs">
                        {step.stepNumber}
                      </span>

                      <div className="p-2.5 rounded-lg bg-secondary text-primary border border-accent/20 group-hover:scale-105 transition-transform duration-300">
                        <Icon className="size-5" />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <span className="text-xs font-semibold uppercase tracking-wider text-accent font-mono">
                        {step.highlight}
                      </span>
                      <CardTitle className="text-xl font-semibold text-foreground font-serif leading-snug">
                        {step.title}
                      </CardTitle>
                    </div>

                    <p className="text-xs font-medium text-muted-foreground mt-1">
                      {step.tagline}
                    </p>
                  </CardHeader>

                  <CardContent className="p-6 pt-0 flex-1 flex flex-col justify-between space-y-6">
                    <p className="text-sm text-foreground/80 leading-relaxed">
                      {step.description}
                    </p>

                    <div className="pt-4 border-t border-border/60">
                      <div className="flex items-start gap-2.5 bg-muted/50 p-3 rounded-lg border border-border/40">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <div className="text-xs text-muted-foreground">
                          <span className="font-semibold text-foreground block mb-0.5">
                            Key Deliverable:
                          </span>
                          {step.deliverable}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="process-cta mt-16 md:mt-20 max-w-4xl mx-auto rounded-2xl bg-muted/60 border border-border p-8 md:p-10 shadow-xs" style={{
        opacity: 0
      }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center md:text-left">
              <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
                <ShieldCheck className="size-4 text-primary" />
                <span>Direct Consultant Access Guaranteed</span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold text-foreground font-serif">
                Ready to transform your search engine visibility?
              </h3>
              <p className="text-sm text-muted-foreground max-w-xl">
                Book a direct consultation with me to discuss your current rankings, organic traffic potential, and customized strategy.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/ContactPage">
                  <span>Book a Free Consultation</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
                <Link to="/SeoAuditsPage">
                  <span>Explore SEO Audits</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}